import { Icon } from "@material-ui/core";
import {
    ActionPermissionsObject,
    IWorkspaceResourceFolder,
    PermissionActionType,
    WorkspacePlanTier,
    WorkspaceResourceFolderType,
    WorkspaceResourceType,
} from "common/interfaces";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import { Layout, PaneBody, PaneContent, ResourcePaneSideBar, SearchBarSort } from "js/react/views/SharedResources/Components";
import PresentationTemplateList, {
    PresentationTemplateDragItem,
} from "js/react/views/SharedResources/Components/PresentationTemplateList";

import Banner from "js/Components/Banner";
import PresentationTemplatesPaneController, {
    PresentationTemplatesPaneControllerState,
} from "js/controllers/PresentationTemplatesPaneController";
import WorkspaceController, { WorkspaceControllerState } from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import getObjectHash from "common/utils/getObjectHash";
import { OnMountedReporter } from "js/core/utilities/useMountedAt";
import { CreatePresentationDialogPaneType } from "js/editor/CreatePresentation/CreatePresentationDialogTypes";
import { PresentationListHeader } from "js/editor/PresentationLibrary/PresentationListHeader";
import { SearchAndAdd, SearchAndAddAction, SearchAndAddFilterOption } from "js/editor/PresentationLibrary/components/SearchAndAdd";
import {
    buildResourcePaneBreadcrumbData,
    PresentationLibraryPaneBreadcrumb,
} from "js/editor/PresentationLibrary/components/PresentationLibraryPaneBreadcrumb";
import { t } from "js/i18n";
import { ShowDialog } from "js/react/components/Dialogs/BaseDialog";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import { FlexBox } from "js/react/components/LayoutGrid";
import { WorkspaceResourceFolderHeaderActions } from "js/react/views/SharedResources/Components/WorkspaceResourceFolderMenu";
import { selectedClass, SideBarItem } from "js/react/views/SharedResources/Components/WorkspaceResourcesSidebarHelper";
import { useFolderDragItems } from "js/react/views/SharedResources/hooks/useFolderDragItems";
import { moveResourcesToFolder } from "js/react/views/SharedResources/utils/folderDropHelpers";
import { isPresentationTemplatesTopLevelEmptyState } from "js/react/views/SharedResources/utils/isPresentationTemplatesTopLevelEmptyState";
import { TeamResourceTypes } from "js/react/views/TeamResources/TeamResources";

const SEARCH_FIELD_ALL = "__all__";
const TAG_FILTER_ALL = "__all__";

const contentFilterOptions: SearchAndAddFilterOption[] = [
    { field: SEARCH_FIELD_ALL, label: t("All Content") },
    { field: "slide:text", label: t("Slide Text") },
    { field: "resource:metadata:name", label: t("Name") },
    { field: "resource:metadata:description", label: t("Description") },
    { field: "resource:tagsText", label: t("Tags") },
];

const sortOptions: SearchBarSort[] = [{
    field: "resource.metadata.name", label: t("Name"), direction: "asc", icon: "sort_by_alpha",
}, {
    field: "resource.metadata.ratedByUsersCount", label: t("Rating"), direction: "desc", icon: "star",
}, {
    field: "resource.createdAt", label: t("Recently Created"), direction: "desc", icon: "calendar_month",
}, {
    field: "document.modifiedAt", label: t("Recently Modified"), direction: "desc", icon: "edit_calendar",
}];

const TemplatesListHeaderRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const TemplatesListHeaderBreadcrumbGroup = styled.div`
    display: flex;
    align-items: center;
    flex-shrink: 0;
    gap: 10px;
`;

const ResultsContainer = styled.div`
    flex: 1;
    width: 100%;
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

const EmbeddedTemplatesContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-height: 0;
    flex: 1;
    gap: 20px;
`;

const SearchControls = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
`;

export interface PresentationTemplatesPaneProps {
    editable?: boolean;
    embedded?: boolean;
    tagFilter?: string;
    onTagFilterChange?: (tag: string) => void;
    onReady?: () => void;
    onSelect?: (presentationId: string | null, isDoubleClick?: boolean, selectedFolderId?: string) => void;
    folderId?: string;
    onSelectFolder?: (pane: string, folderId?: string) => void;
}

type PresentationTemplatesPaneComponentProps =
    WorkspaceControllerState &
    PresentationTemplatesPaneControllerState &
    PresentationTemplatesPaneProps;

function PresentationTemplatesPane({
    editable = true,
    embedded = false,
    tagFilter: externalTagFilter,
    onTagFilterChange,
    onSelect,
    onReady,
    data,
    fetching,
    search,
    sort,
    tags,
    resourceFolders,
    folderId,
    onSelectFolder
}: PresentationTemplatesPaneComponentProps) {
    const [isInitializedForCurrentMount, setIsInitializedForCurrentMount] = useState(false);
    const [hasCompletedInitialLoad, setHasCompletedInitialLoad] = useState(false);
    const [internalSelectedTag, setInternalSelectedTag] = useState(TAG_FILTER_ALL);
    const isControlledTagFilter = onTagFilterChange != null;
    const selectedTag = isControlledTagFilter ? (externalTagFilter ?? TAG_FILTER_ALL) : internalSelectedTag;
    const setSelectedTag = isControlledTagFilter ? onTagFilterChange : setInternalSelectedTag;
    const [selectedFolderId, setSelectedFolderId] = useState<string | null>(folderId ?? null);
    const {
        draggedItems,
        handleStartDragItems,
        handleDropDragItems,
        clearDragItems,
        getActiveDragItems,
    } = useFolderDragItems(({ resourceId, resourceType }: PresentationTemplateDragItem) => ({
        resourceId,
        resourceType,
    }));

    useEffect(() => {
        setSelectedFolderId(folderId ?? null);
    }, [folderId]);

    // initialize() resolves while the first page is still loading, so the pane is only done
    // loading once that first fetch settles.
    useEffect(() => {
        if (isInitializedForCurrentMount && !fetching) {
            setHasCompletedInitialLoad(true);
        }
    }, [isInitializedForCurrentMount, fetching]);

    const controllerFilters = (() => {
        const nextFilters = [];

        if (!editable) {
            nextFilters.push({
                field: "resource.metadata.isPublished",
                value: true,
                operator: "eq",
            });
        }

        if (selectedTag !== TAG_FILTER_ALL) {
            nextFilters.push({
                field: "resource.tags",
                operator: "eq",
                value: selectedTag,
            });
        }

        if (selectedFolderId) {
            nextFilters.push({
                field: "resource.folderIds",
                operator: "eq",
                value: selectedFolderId,
            });
        }

        return nextFilters;
    })();

    useEffect(() => {
        let isMounted = true;

        void PresentationTemplatesPaneController.initialize({
            workspaceId: WorkspaceController.workspaceId,
            sort: [{
                field: "document.modifiedAt",
                direction: "desc",
            }],
            filters: controllerFilters,
        }).then(() => {
            if (isMounted) {
                setIsInitializedForCurrentMount(true);
            }
        });

        return () => {
            isMounted = false;
            PresentationTemplatesPaneController.reset();
        };
    }, []);

    useEffect(() => {
        if (!PresentationTemplatesPaneController.initialized) {
            return;
        }

        PresentationTemplatesPaneController.setFilters(controllerFilters);
    }, [getObjectHash(controllerFilters)]);

    const clearSearchQuery = useCallback(() => {
        if (search.query) {
            PresentationTemplatesPaneController.setSearch({
                query: "",
                fields: search.fields,
            });
        }
    }, [search.fields, search.query]);

    const handleSearch = (searchTerm: string) => {
        PresentationTemplatesPaneController.setSearch({
            query: searchTerm?.trim(),
            fields: search.fields,
        });
    };

    const handleSort = (nextSort: SearchBarSort) => {
        PresentationTemplatesPaneController.setSort([{
            field: nextSort.field,
            direction: nextSort.direction,
        }]);
    };

    const handleContentFilterChange = (event: { target: { value: string } }) => {
        const searchField = event.target.value;
        PresentationTemplatesPaneController.setSearch({
            query: search.query,
            fields: searchField === SEARCH_FIELD_ALL ? undefined : [searchField],
        });
    };

    const handleTagFilterChange = (event: { target: { value: string } }) => {
        setSelectedTag(event.target.value);
    };

    const handleCreateTemplate = (pane: CreatePresentationDialogPaneType, { hideSidebar = false, createInFolder = false } = {}) => {
        const folderIdForCreate = createInFolder ? selectedFolderId : null;

        // Top-level create should leave the folder view so the new template lands at root.
        if (!createInFolder && selectedFolderId) {
            setSelectedFolderId(null);
            clearSearchQuery();
            onSelectFolder?.(TeamResourceTypes.PRESENTATION_TEMPLATES);
        }

        AppController.showCreatePresentationDialog({
            pane,
            hideSidebar,
            isTeamTemplate: true,
            folderId: folderIdForCreate,
        });
    };

    const showCreateWithAiButton = WorkspaceController.actionPermissions[ActionPermissionsObject.GENERATIVE_AI].use &&
    !WorkspaceController.workspaceIsPastDue;

    const searchActions: SearchAndAddAction[] = editable ? [
        ...(showCreateWithAiButton ? [{
            id: "create-template-with-ai-button",
            label: t("Create with AI"),
            onClick: () => handleCreateTemplate(CreatePresentationDialogPaneType.GENERATE_WITH_AI, { hideSidebar: true }),
            variant: "ai",
        }] : []),
        {
            label: t("New Template"),
            onClick: () => handleCreateTemplate(CreatePresentationDialogPaneType.BLANK_PRESENTATION),
            variant: "primary",
        },
    ] : [];

    const handleFolderDrop = useCallback(async (folder: IWorkspaceResourceFolder) => {
        const items = getActiveDragItems();
        if (!items.length) {
            return;
        }

        if (folder.id !== selectedFolderId) {
            setSelectedFolderId(folder.id);
            clearSearchQuery();
            onSelectFolder?.(TeamResourceTypes.PRESENTATION_TEMPLATES, folder.id);
        }

        clearDragItems();

        await moveResourcesToFolder({
            folder,
            resources: items,
            progressTitle: t("Moving template to folder..."),
            progressTitlePlural: count => t("Moving {{count}} templates to folder...", { count }),
            errorMessage: t("An error occurred while moving templates to the folder."),
            logTag: "PresentationTemplatesPane",
        });
    }, [clearDragItems, clearSearchQuery, getActiveDragItems, onSelectFolder, selectedFolderId]);

    const handleSelectSidebarFolder = useCallback((nextFolderId: string | null) => {
        setSelectedFolderId(nextFolderId);
        clearSearchQuery();
        onSelectFolder?.(TeamResourceTypes.PRESENTATION_TEMPLATES, nextFolderId ?? undefined);
    }, [clearSearchQuery, onSelectFolder]);

    const handleNavigateToFolder = useCallback(({ folderId: nextFolderId, subFolderId }: { folderId: string; subFolderId?: string }) => {
        const nextSelectedFolderId = subFolderId ?? nextFolderId;
        setSelectedFolderId(nextSelectedFolderId);
        clearSearchQuery();
        onSelectFolder?.(TeamResourceTypes.PRESENTATION_TEMPLATES, nextSelectedFolderId);
    }, [clearSearchQuery, onSelectFolder]);

    const handleSelectAllTemplates = useCallback(() => {
        if (!selectedFolderId && !folderId) {
            return;
        }

        clearSearchQuery();
        setSelectedFolderId(null);
        onSelectFolder?.(TeamResourceTypes.PRESENTATION_TEMPLATES);
    }, [clearSearchQuery, folderId, onSelectFolder, selectedFolderId]);

    const hasPresentationTemplateFolders = useMemo(
        () => resourceFolders.some(
            folder => folder.resourcesType === WorkspaceResourceType.PRESENTATION_TEMPLATE && !folder.parentFolderId
        ),
        [resourceFolders]
    );

    const sortState: SearchBarSort = {
        field: sort[0]?.field ?? "document.modifiedAt",
        direction: sort[0]?.direction ?? "desc",
    };

    const selectedContentFilter = search.fields?.[0] ?? SEARCH_FIELD_ALL;
    const tagFilterOptions = useMemo<SearchAndAddFilterOption[]>(() => [
        { field: TAG_FILTER_ALL, label: t("All Tags") },
        ...tags.map(({ tag, count }) => ({
            field: tag,
            label: `${tag} (${count})`,
        })),
    ], [tags]);

    const canUseSharedResources = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].use;
    const canUpgradeToManageSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(
        ActionPermissionsObject.SHARED_RESOURCES,
        PermissionActionType.MANAGE
    );
    const shouldHideTopLevelSideBar = isPresentationTemplatesTopLevelEmptyState({
        dataLength: data.length,
        searchQuery: search.query,
        selectedTag,
        selectedFolderId,
        folderId,
        hasPresentationTemplateFolders,
    });
    // Sidebar and header both wait for the first fetch to settle so the loading spinner stands
    // alone and the pane chrome appears in one step instead of piece by piece.
    const shouldRenderSideBar = !embedded && hasCompletedInitialLoad && !shouldHideTopLevelSideBar;
    const shouldHideTopLevelHeader = !hasCompletedInitialLoad;

    const sideBarCategoryNav = useMemo(() => (
        <SideBarItem
            className={selectedClass(selectedFolderId === null)}
            id="all-templates"
            onClick={handleSelectAllTemplates}
        >
            <Icon className="material-symbols-outlined">select_all</Icon>
            {t("All templates")}
        </SideBarItem>
    ), [handleSelectAllTemplates, selectedFolderId]);

    const handleSelect = (presentationId: string | null, isDoubleClick?: boolean) => {
        onSelect?.(presentationId, isDoubleClick, selectedFolderId);
    };

    const handleBreadcrumbSelectFolder = (nextFolderId: string | null) => {
        setSelectedFolderId(nextFolderId);
        clearSearchQuery();
        onSelectFolder?.(TeamResourceTypes.PRESENTATION_TEMPLATES, nextFolderId ?? undefined);
    };

    const breadcrumb = useMemo(() => buildResourcePaneBreadcrumbData({
        rootLabel: t("Presentations"),
        selectedFolderId,
        resourceFolders,
        onSelectFolder: handleBreadcrumbSelectFolder,
    }), [selectedFolderId, resourceFolders]);

    const templateList = (
        <>
            <ResultsContainer>
                <PresentationTemplateList
                    editable={editable}
                    allowDragging={editable && canUseSharedResources}
                    selectedFolderId={selectedFolderId}
                    onSelect={handleSelect}
                    onStartDragItems={handleStartDragItems}
                    onDropDragItems={handleDropDragItems}
                    resourceFolders={resourceFolders}
                    onNavigateToFolder={handleNavigateToFolder}
                />
            </ResultsContainer>
        </>
    );

    const upgradeBanner = canUpgradeToManageSharedResources && (
        <FlexBox center fillWidth>
            <Banner
                headerText={t("Unlock Team Presentation Templates")}
                bodyText={t("Upgrade to a Team plan to give your team a head start with a library of presentation templates.")}
                buttonLabel={t("Upgrade")}
                onButtonClick={() => ShowDialog(ChangePlanDialog,
                    {
                        featuredPlan: WorkspacePlanTier.TEAM,
                        analytics: { cta: "team_presentation_templates" }
                    })}
            />
        </FlexBox>
    );

    if (embedded) {
        return (
            <EmbeddedTemplatesContainer>
                {onReady && <OnMountedReporter onMounted={onReady} />}
                {upgradeBanner}
                {templateList}
            </EmbeddedTemplatesContainer>
        );
    }

    return (
        <Layout>
            {onReady && <OnMountedReporter onMounted={onReady} />}

            {canUseSharedResources && !shouldHideTopLevelHeader && (<>
                {canUseSharedResources && (
                    <SearchControls>
                        <SearchAndAdd
                            onSearch={handleSearch}
                            searchValue={search.query ?? ""}
                            searchPlaceholder={t("Search team templates...")}
                            contentFilterOptions={contentFilterOptions}
                            selectedContentFilter={selectedContentFilter}
                            onContentFilterChange={handleContentFilterChange}
                            tagFilterOptions={tagFilterOptions}
                            selectedTagFilter={selectedTag}
                            onTagFilterChange={handleTagFilterChange}
                            actions={searchActions}
                        />
                    </SearchControls>
                )}
            </>)}

            {upgradeBanner}

            <PaneBody>
                <ResourcePaneSideBar
                    visible={shouldRenderSideBar}
                    readOnly={!editable}
                    resourceTypes={[WorkspaceResourceType.PRESENTATION_TEMPLATE]}
                    folderType={WorkspaceResourceFolderType.SHARED}
                    createResourcesType={WorkspaceResourceType.PRESENTATION_TEMPLATE}
                    resourceFolders={resourceFolders}
                    selectedFolderId={selectedFolderId}
                    draggedItems={draggedItems}
                    onFolderDrop={handleFolderDrop}
                    onSelectFolder={handleSelectSidebarFolder}
                >
                    {sideBarCategoryNav}
                </ResourcePaneSideBar>

                <PaneContent>
                    {!shouldHideTopLevelHeader && (
                        <TemplatesListHeaderRow>
                            <TemplatesListHeaderBreadcrumbGroup>
                                <PresentationLibraryPaneBreadcrumb {...breadcrumb} />
                                {editable && selectedFolderId && (
                                    <WorkspaceResourceFolderHeaderActions
                                        folderId={selectedFolderId}
                                        resourceTypes={[WorkspaceResourceType.PRESENTATION_TEMPLATE]}
                                        resourceFolders={resourceFolders}
                                        createResourcesType={WorkspaceResourceType.PRESENTATION_TEMPLATE}
                                        onSelectFolder={handleSelectSidebarFolder}
                                        onCreateItem={() => handleCreateTemplate(CreatePresentationDialogPaneType.BLANK_PRESENTATION, { createInFolder: true })}
                                        showCreateButton
                                    />
                                )}
                            </TemplatesListHeaderBreadcrumbGroup>
                            {canUseSharedResources && (
                                <PresentationListHeader
                                    sortOptions={sortOptions}
                                    sort={sortState}
                                    searchQuery={search.query ?? ""}
                                    onSetSort={handleSort}
                                    showViewTypeToggle={false}
                                />
                            )}
                        </TemplatesListHeaderRow>
                    )}
                    {templateList}
                </PaneContent>
            </PaneBody>
        </Layout>
    );
}

export default WorkspaceController.withInitializedState(
    PresentationTemplatesPaneController.withState(PresentationTemplatesPane)
);
